import React, { PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import { gql } from '@yi/core';
import { i18n_at_home_practice } from '@yi/i18n/phrases/i18n_at_home_practice';
import { i18n_class } from '@yi/i18n/phrases/i18n_class';
import { i18n_meditation } from '@yi/i18n/phrases/i18n_meditation';
import { i18n_min } from '@yi/i18n/phrases/i18n_min';

import { utilities } from '~/common';
import { useCurrentUser } from '~/src/common/user';
import { lightGrey } from '~/styles';

import { AuthorText, Duration, StyleText, Title } from './elements';
import { AccessTime, Avatar, DurationIconButton } from '../../ClassIconComponents';
import { ClassesIcon, MeditationsIcon } from '../../icons';
import { LazyLoadWrapper } from '../../LazyLoadWrapper';
import { useLocale } from '../../LocaleProvider';
import { BaseCard, BaseCardProps } from '../bases';
import { Author, CardContent, ClassImageOverlay } from '../elements';

const { truncate } = utilities;

type Item = gql.typesClient.Query_CurrentUser_CurrentUser_recent_user_created_activity;
interface Props extends Omit<Partial<BaseCardProps>, 'onClick'> {
  item: Item;
}

export const UserCreatedPracticeCard: React.FC<PropsWithChildren<Props>> = ({ item, ...props }) => {
  const { locale } = useLocale();

  const { type, minutes } = item;
  const { currentUser } = useCurrentUser();
  if (!currentUser) return null;
  const truncatedUserName = truncate(
    `${currentUser.first_name} ${currentUser.last_name}`,
    23,
    '...',
  );
  return (
    <BaseCard
      onClick={e => e.preventDefault()}
      dimension={{ width: 282, height: 320 }}
      url={''}
      thumb={''}
      imageHeight={194}
      bgImageFull={false}
      renderImageOverlay={() => (
        <ClassImageOverlay style={{ height: '100%', width: '100%', background: lightGrey }}>
          {type === 'CLASS' && (
            <ClassesIcon style={{ height: '100px', width: '100px', color: 'white' }} />
          )}
          {type === 'MEDITATION' && (
            <MeditationsIcon style={{ height: '100px', width: '100px', color: 'white' }} />
          )}
        </ClassImageOverlay>
      )}
      renderProgressBarInMedia={() => undefined}
      renderContent={() => (
        <CardContent>
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <StyleText>{i18n_at_home_practice(locale)}</StyleText>
            </Box>
          </Box>
          <Box display="flex">
            <Title>
              {type === 'CLASS' && i18n_class(locale)}
              {type === 'MEDITATION' && i18n_meditation(locale)}
            </Title>
          </Box>
        </CardContent>
      )}
      renderUpperLeft={() => undefined}
      renderUpperRight={() => undefined}
      renderLowerLeft={() => (
        <Author>
          <LazyLoadWrapper enabled={props.lazyLoad} width={32} height={24}>
            <Avatar
              imgProps={{ role: 'presentation' }}
              src={currentUser.image?.processed_url || ''}
            />
          </LazyLoadWrapper>
          <AuthorText>{truncatedUserName}</AuthorText>
        </Author>
      )}
      renderLowerRight={() => (
        <DurationIconButton aria-label="Settings">
          <AccessTime />
          <Duration>
            {minutes}
            {i18n_min(locale)}
          </Duration>
        </DurationIconButton>
      )}
      {...props}
    />
  );
};
